import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import UserProfile from '../..';
import UserPayroll from '../index';
import Button from '../../../../components/ui/Button';
import { Spinner } from '../../../../components/ui/Spinner';
import AppIcon from '../../../../components/AppIcon';
import { getUser } from '../../../../api/General/account';
import {
  addUserHourlyRate,
  deleteUserHourlyRate,
  getUserHourlyRates,
  updateUserHourlyRate,
  updateUserPayBasis,
  type UserHourlyRateInput,
  type UserHourlyRateView,
} from '../../../../api/Payroll/hourlyRate';
import type { PayBasis, UserView } from '../../../../models/general';
import { getApiErrorMessage } from '../../../../utils/apiErrors';
import { EMPTY_VALUE, dayKey, formatDay, formatMoney } from '../../../../components/reports';
import { useHourlyPayAccess } from '../../../../utils/hourlyPayAccess';
import { cn } from '../../../../utils/cn';
import { DateField } from '../../../../components/dateFields';

/**
 * PROFILE › PAYROLL › HOURLY PAY — HR ONLY (docs/hourly-pay-spec.md §3.1, §3.2, §10).
 *
 * Two things about ONE employee:
 *   1. Pay basis (Salaried / Hourly). The switch every hourly behaviour keys off. It is NOT the
 *      employment type — the page may SUGGEST hourly for a part-timer, it never infers it.
 *   2. The hourly rate history: effective-dated, non-overlapping, encrypted server-side. A shift can
 *      only be approved when a rate covers its work date (400 noHourlyRate otherwise), so the page
 *      says plainly when the employee is hourly with no rate for today.
 *
 * Both are salary data: the route is HR_ONLY_ROLES and the nav item is Roles.HR. Nothing here is
 * rendered for Admin, Manager or the employee.
 *
 * Editing is INLINE (a row editor), not a modal, and switching the pay basis asks for an inline
 * confirmation because it moves the employee off the overtime path (D4).
 */

const INPUT_CLASSES =
  'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm tabular-nums ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary ' +
  'transition-colors disabled:cursor-not-allowed disabled:opacity-50';

interface RateDraft {
  /** 0 = new row. */
  id: number;
  rate: string;
  effectiveStart: string;
  effectiveStop: string;
}

const PAY_BASES: PayBasis[] = ['Salaried', 'Hourly'];

/** Inclusive date ranges on `yyyy-MM-dd` strings; an empty stop is open-ended. */
function overlaps(aStart: string, aStop: string | null | undefined, bStart: string, bStop: string | null | undefined): boolean {
  const aEnd = aStop || '9999-12-31';
  const bEnd = bStop || '9999-12-31';
  return aStart <= bEnd && bStart <= aEnd;
}

function covers(rate: UserHourlyRateView, day: string): boolean {
  return rate.effectiveStart <= day && (!rate.effectiveStop || rate.effectiveStop >= day);
}

const HourlyPay: React.FC = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { id } = useParams();
  const userId = id ?? '';
  const access = useHourlyPayAccess();
  const company = useSelector(
    (state: { user?: { company?: { currencyId?: number; currency?: { currencyCode?: string } } | null } }) =>
      state.user?.company ?? null,
  );
  const companyCurrencyId = company?.currencyId ?? 0;
  const companyCurrencyCode = company?.currency?.currencyCode;

  const [user, setUser] = useState<UserView | null>(null);
  const [rates, setRates] = useState<UserHourlyRateView[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [pendingBasis, setPendingBasis] = useState<PayBasis | null>(null);
  const [savingBasis, setSavingBasis] = useState(false);
  const [basisMessage, setBasisMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  const [draft, setDraft] = useState<RateDraft | null>(null);
  const [savingRate, setSavingRate] = useState(false);
  const [rateError, setRateError] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const [userResult, ratesResult] = await Promise.all([getUser(userId), getUserHourlyRates(userId)]);
      if (userResult.errors.length > 0) {
        setLoadError(getApiErrorMessage(userResult, t, t('hourlyRate.loadFailed')));
        return;
      }
      if (ratesResult.errors.length > 0) {
        setLoadError(getApiErrorMessage(ratesResult, t, t('hourlyRate.loadFailed')));
        return;
      }
      setUser(userResult.payload);
      // Normalised to `yyyy-MM-dd` so the range checks below compare dates, whether the server sends
      // a DateOnly or a midnight DateTime.
      const normalised = (ratesResult.payload ?? []).map((r) => ({
        ...r,
        effectiveStart: (r.effectiveStart ?? '').slice(0, 10),
        effectiveStop: r.effectiveStop ? r.effectiveStop.slice(0, 10) : null,
      }));
      setRates(normalised.sort((a, b) => b.effectiveStart.localeCompare(a.effectiveStart)));
    } catch (err) {
      setLoadError(getApiErrorMessage(err, t, t('hourlyRate.loadFailed')));
    } finally {
      setLoading(false);
    }
  }, [userId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const currentBasis: PayBasis = user?.payBasis === 'Hourly' ? 'Hourly' : 'Salaried';
  const today = dayKey(new Date());
  const rateToday = useMemo(() => rates.find((r) => covers(r, today)) ?? null, [rates, today]);
  const suggestsHourly =
    currentBasis === 'Salaried' && /part[\s-]?time|hourly/i.test(user?.employmentType ?? '');

  /* ── pay basis ──────────────────────────────────────────────────────────────────────────────── */

  const confirmBasis = async () => {
    if (!user || !pendingBasis) return;
    setSavingBasis(true);
    setBasisMessage(null);
    try {
      const result = await updateUserPayBasis(user, pendingBasis);
      if (result.errors.length > 0) {
        setBasisMessage({ tone: 'error', text: getApiErrorMessage(result, t, t('hourlyRate.basisSaveFailed')) });
        return;
      }
      setUser((prev) => (prev ? { ...prev, payBasis: pendingBasis } : prev));
      setBasisMessage({
        tone: 'ok',
        text: pendingBasis === 'Hourly' ? t('hourlyRate.basisSavedHourly') : t('hourlyRate.basisSavedSalaried'),
      });
      setPendingBasis(null);
      // Re-read so the page reflects what the server actually stored (a server that ignores the
      // field would otherwise look saved).
      const fresh = await getUser(user.id).catch(() => null);
      if (fresh && fresh.errors.length === 0 && fresh.payload) setUser(fresh.payload);
    } catch (err) {
      setBasisMessage({ tone: 'error', text: getApiErrorMessage(err, t, t('hourlyRate.basisSaveFailed')) });
    } finally {
      setSavingBasis(false);
    }
  };

  /* ── rate editor ────────────────────────────────────────────────────────────────────────────── */

  const draftChecks = useMemo(() => {
    if (!draft) return null;
    const rateNum = Number(draft.rate);
    const rateOk = draft.rate.trim() !== '' && Number.isFinite(rateNum) && rateNum > 0;
    const startOk = /^\d{4}-\d{2}-\d{2}$/.test(draft.effectiveStart);
    const stopOk = !draft.effectiveStop || (startOk && draft.effectiveStop >= draft.effectiveStart);
    const noOverlap =
      !startOk ||
      !rates.some(
        (r) => r.id !== draft.id && overlaps(draft.effectiveStart, draft.effectiveStop || null, r.effectiveStart, r.effectiveStop),
      );
    return { rateOk, startOk, stopOk, noOverlap };
  }, [draft, rates]);
  const draftValid = !!draftChecks && Object.values(draftChecks).every(Boolean);

  const startAdd = () => {
    setRateError(null);
    setConfirmDeleteId(null);
    // Defaults to today; the overlap rule below says at once if an open-ended row must be closed first.
    setDraft({ id: 0, rate: '', effectiveStart: today, effectiveStop: '' });
  };

  const startEdit = (row: UserHourlyRateView) => {
    setRateError(null);
    setConfirmDeleteId(null);
    setDraft({
      id: row.id,
      rate: String(row.rate),
      effectiveStart: row.effectiveStart.slice(0, 10),
      effectiveStop: row.effectiveStop ? row.effectiveStop.slice(0, 10) : '',
    });
  };

  const saveDraft = async () => {
    if (!draft || !draftValid || !userId) return;
    setSavingRate(true);
    setRateError(null);
    const input: UserHourlyRateInput = {
      rate: Number(draft.rate),
      // v1: the company currency (the server defaults it and refuses any other).
      ...(companyCurrencyId ? { currencyId: companyCurrencyId } : {}),
      effectiveStart: draft.effectiveStart,
      effectiveStop: draft.effectiveStop,
    };
    try {
      const result = draft.id
        ? await updateUserHourlyRate(userId, draft.id, input)
        : await addUserHourlyRate(userId, input);
      if (result.errors.length > 0) {
        setRateError(getApiErrorMessage(result, t, t('hourlyRate.saveFailed')));
        return;
      }
      setDraft(null);
      await load();
    } catch (err) {
      setRateError(getApiErrorMessage(err, t, t('hourlyRate.saveFailed')));
    } finally {
      setSavingRate(false);
    }
  };

  const removeRate = async (rateId: number) => {
    setSavingRate(true);
    setRateError(null);
    try {
      const result = await deleteUserHourlyRate(userId, rateId);
      if (result.errors.length > 0) {
        setRateError(getApiErrorMessage(result, t, t('hourlyRate.deleteFailed')));
        return;
      }
      setConfirmDeleteId(null);
      await load();
    } catch (err) {
      setRateError(getApiErrorMessage(err, t, t('hourlyRate.deleteFailed')));
    } finally {
      setSavingRate(false);
    }
  };

  const rule = (ok: boolean, text: string) => (
    <li className="flex items-start gap-1.5">
      <AppIcon name={ok ? 'Check' : 'X'} size={13} className={cn('mt-0.5 shrink-0', ok ? 'text-success-strong' : 'text-muted-foreground')} />
      <span className={cn(ok ? 'text-muted-foreground' : 'text-destructive-strong')}>{text}</span>
    </li>
  );

  const editorRow = (key: string) =>
    draft &&
    draftChecks && (
      <tr key={key} className="border-t border-border bg-primary/[0.03]" data-testid="hourly-rate-editor">
        <td colSpan={4} className="px-4 py-3">
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <label htmlFor="hourly-rate-value" className="text-xs font-medium text-foreground">
                {t('hourlyRate.rateLabel', { currency: companyCurrencyCode ?? '' })}
              </label>
              <input
                id="hourly-rate-value"
                type="number"
                inputMode="decimal"
                step="0.01"
                min={0}
                value={draft.rate}
                disabled={savingRate}
                onChange={(e) => setDraft({ ...draft, rate: e.target.value })}
                aria-invalid={!draftChecks.rateOk || undefined}
                className={cn(INPUT_CLASSES, 'mt-1.5', !draftChecks.rateOk && draft.rate !== '' && 'border-destructive')}
                data-testid="hourly-rate-input"
              />
            </div>
            <div>
              <label htmlFor="hourly-rate-start" className="text-xs font-medium text-foreground">
                {t('hourlyRate.fromLabel')}
              </label>
              <DateField
                id="hourly-rate-start"
                value={draft.effectiveStart}
                disabled={savingRate}
                onChange={(v) => setDraft({ ...draft, effectiveStart: v })}
                className="mt-1.5"
                data-testid="hourly-rate-start"
              />
            </div>
            <div>
              <label htmlFor="hourly-rate-stop" className="text-xs font-medium text-foreground">
                {t('hourlyRate.toLabel')}
              </label>
              <DateField
                id="hourly-rate-stop"
                value={draft.effectiveStop}
                min={draft.effectiveStart || undefined}
                disabled={savingRate}
                onChange={(v) => setDraft({ ...draft, effectiveStop: v })}
                className="mt-1.5"
                data-testid="hourly-rate-stop"
              />
              <p className="mt-1 text-[11px] text-muted-foreground">{t('hourlyRate.toHint')}</p>
            </div>
          </div>
          <ul className="mt-3 grid gap-1 text-xs sm:grid-cols-2">
            {rule(draftChecks.rateOk, t('hourlyRate.ruleRatePositive'))}
            {rule(draftChecks.startOk, t('hourlyRate.ruleStartRequired'))}
            {rule(draftChecks.stopOk, t('hourlyRate.ruleStopAfterStart'))}
            {rule(draftChecks.noOverlap, t('hourlyRate.ruleNoOverlap'))}
          </ul>
          {rateError && (
            <p role="alert" className="mt-3 text-sm text-destructive-strong">
              {rateError}
            </p>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              size="sm"
              loading={savingRate}
              disabled={!draftValid || savingRate}
              onClick={() => void saveDraft()}
              data-testid="hourly-rate-save"
            >
              {draft.id ? t('hourlyRate.saveEdit') : t('hourlyRate.saveNew')}
            </Button>
            <Button variant="ghost" size="sm" disabled={savingRate} onClick={() => setDraft(null)}>
              {t('hourlyRate.cancel')}
            </Button>
          </div>
        </td>
      </tr>
    );

  /* ── render ─────────────────────────────────────────────────────────────────────────────────── */

  const body = () => {
    if (loading && !user) {
      return (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      );
    }
    if (loadError) {
      return (
        <div role="alert" className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive-strong">
          {loadError}
          <button type="button" onClick={() => void load()} className="ms-3 font-medium underline underline-offset-2">
            {t('hourlyRate.retry')}
          </button>
        </div>
      );
    }
    const shownBasis = pendingBasis ?? currentBasis;
    return (
      <div className="space-y-6">
        {!access.companyEnabled && access.known && (
          <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
            <AppIcon name="Info" size={16} className="mt-0.5 shrink-0" />
            <p className="max-w-[80ch]">
              {t('hourlyRate.companyOff')}{' '}
              <Link to="/settings/payroll/hourly-pay" className="font-medium text-primary underline underline-offset-2">
                {t('hourlyRate.companyOffLink')}
              </Link>
            </p>
          </div>
        )}

        {/* 1 — pay basis */}
        <section aria-labelledby="pay-basis-heading" className="rounded-xl border border-border bg-card">
          <header className="border-b border-border px-5 py-4">
            <h3 id="pay-basis-heading" className="text-base font-semibold text-foreground">
              {t('hourlyRate.basisTitle')}
            </h3>
            <p className="mt-1 max-w-[70ch] text-sm text-muted-foreground">{t('hourlyRate.basisIntro')}</p>
          </header>
          <div className="px-5 py-4">
            <div role="radiogroup" aria-labelledby="pay-basis-heading" className="grid gap-2 sm:grid-cols-2">
              {PAY_BASES.map((basis) => {
                const selected = shownBasis === basis;
                return (
                  <button
                    key={basis}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    disabled={savingBasis}
                    data-testid={`pay-basis-${basis}`}
                    onClick={() => {
                      setBasisMessage(null);
                      setPendingBasis(basis === currentBasis ? null : basis);
                    }}
                    className={cn(
                      'rounded-lg border p-3 text-start transition-colors duration-150',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20',
                      selected ? 'border-primary bg-primary/5' : 'border-border hover:border-muted-foreground/50 hover:bg-muted/40',
                    )}
                  >
                    <span className="flex items-center gap-2">
                      <span
                        className={cn('grid h-4 w-4 shrink-0 place-items-center rounded-full border', selected ? 'border-primary' : 'border-input')}
                        aria-hidden="true"
                      >
                        {selected && <span className="h-2 w-2 rounded-full bg-primary" />}
                      </span>
                      <span className={cn('text-sm font-medium', selected ? 'text-primary' : 'text-foreground')}>
                        {t(`hourlyRate.basis.${basis}`)}
                      </span>
                      {basis === currentBasis && (
                        <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium leading-4 text-muted-foreground">
                          {t('hourlyRate.current')}
                        </span>
                      )}
                    </span>
                    <span className="mt-1.5 block ps-6 text-xs text-muted-foreground">{t(`hourlyRate.basisBody.${basis}`)}</span>
                  </button>
                );
              })}
            </div>

            {suggestsHourly && !pendingBasis && (
              <p className="mt-3 flex items-start gap-1.5 text-xs text-muted-foreground">
                <AppIcon name="Lightbulb" size={13} className="mt-0.5 shrink-0" />
                {t('hourlyRate.suggestHourly', { type: user?.employmentType ?? '' })}
              </p>
            )}

            {pendingBasis && (
              <div className="mt-4 rounded-lg border border-warning/40 bg-warning/10 p-3" data-testid="pay-basis-confirm">
                <p className="text-sm font-medium text-foreground">
                  {pendingBasis === 'Hourly'
                    ? t('hourlyRate.confirmHourlyTitle', { name: user?.fullName ?? '' })
                    : t('hourlyRate.confirmSalariedTitle', { name: user?.fullName ?? '' })}
                </p>
                <p className="mt-1 max-w-[75ch] text-sm text-muted-foreground">
                  {pendingBasis === 'Hourly' ? t('hourlyRate.confirmHourlyBody') : t('hourlyRate.confirmSalariedBody')}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    loading={savingBasis}
                    disabled={savingBasis}
                    onClick={() => void confirmBasis()}
                    data-testid="pay-basis-save"
                  >
                    {pendingBasis === 'Hourly' ? t('hourlyRate.confirmHourly') : t('hourlyRate.confirmSalaried')}
                  </Button>
                  <Button variant="ghost" size="sm" disabled={savingBasis} onClick={() => setPendingBasis(null)}>
                    {t('hourlyRate.cancel')}
                  </Button>
                </div>
              </div>
            )}

            {basisMessage && (
              <p
                role={basisMessage.tone === 'error' ? 'alert' : 'status'}
                className={cn('mt-3 text-sm', basisMessage.tone === 'error' ? 'text-destructive-strong' : 'text-success-strong')}
              >
                {basisMessage.text}
              </p>
            )}

            {currentBasis === 'Hourly' && !rateToday && (
              <div className="mt-4 flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive-strong" data-testid="hourly-no-rate-today">
                <AppIcon name="AlertTriangle" size={16} className="mt-0.5 shrink-0" />
                <p className="max-w-[75ch]">{t('hourlyRate.noRateToday')}</p>
              </div>
            )}
          </div>
        </section>

        {/* 2 — rate history */}
        <section aria-labelledby="hourly-rate-heading" className="overflow-hidden rounded-xl border border-border bg-card">
          <header className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-5 py-4">
            <div className="min-w-0">
              <h3 id="hourly-rate-heading" className="text-base font-semibold text-foreground">
                {t('hourlyRate.historyTitle')}
              </h3>
              <p className="mt-1 max-w-[70ch] text-sm text-muted-foreground">{t('hourlyRate.historyIntro')}</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              iconName="Plus"
              disabled={savingRate || (draft !== null && draft.id === 0)}
              onClick={startAdd}
              data-testid="hourly-rate-add"
            >
              {t('hourlyRate.add')}
            </Button>
          </header>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[36rem] text-sm" data-testid="hourly-rate-table">
              <thead className="bg-muted/30">
                <tr className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-2 text-start font-medium">{t('hourlyRate.colRate')}</th>
                  <th className="px-2 py-2 text-start font-medium">{t('hourlyRate.colFrom')}</th>
                  <th className="px-2 py-2 text-start font-medium">{t('hourlyRate.colTo')}</th>
                  <th className="relative px-4 py-2 text-end font-medium">
                    <span className="sr-only">{t('hourlyRate.colActions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {draft && draft.id === 0 && editorRow('new')}
                {rates.length === 0 && !(draft && draft.id === 0) && (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-sm text-muted-foreground">
                      {t('hourlyRate.empty')}
                    </td>
                  </tr>
                )}
                {rates.map((row) =>
                  draft && draft.id === row.id ? (
                    editorRow(`edit-${row.id}`)
                  ) : (
                    <tr key={row.id} className="border-t border-border/60" data-testid={`hourly-rate-row-${row.id}`}>
                      <td className="px-4 py-2.5">
                        <span className="font-medium tabular-nums text-foreground">
                          {t('hourlyRate.perHour', { value: formatMoney(row.rate, row.currencyCode ?? companyCurrencyCode, lang) })}
                        </span>
                        {covers(row, today) && (
                          <span className="ms-2 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                            {t('hourlyRate.inEffect')}
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-2.5 tabular-nums">{formatDay(row.effectiveStart, lang)}</td>
                      <td className="px-2 py-2.5 tabular-nums text-muted-foreground">
                        {row.effectiveStop ? formatDay(row.effectiveStop, lang) : t('hourlyRate.openEnded')}
                      </td>
                      <td className="px-4 py-2.5">
                        {confirmDeleteId === row.id ? (
                          <span className="flex flex-wrap items-center justify-end gap-2">
                            <span className="text-xs text-muted-foreground">{t('hourlyRate.deleteConfirm')}</span>
                            <Button
                              variant="destructive"
                              size="xs"
                              loading={savingRate}
                              disabled={savingRate}
                              onClick={() => void removeRate(row.id)}
                              data-testid={`hourly-rate-delete-confirm-${row.id}`}
                            >
                              {t('hourlyRate.deleteYes')}
                            </Button>
                            <Button variant="ghost" size="xs" disabled={savingRate} onClick={() => setConfirmDeleteId(null)}>
                              {t('hourlyRate.cancel')}
                            </Button>
                          </span>
                        ) : (
                          <span className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="xs"
                              iconName="Pencil"
                              disabled={savingRate || draft !== null}
                              onClick={() => startEdit(row)}
                              aria-label={t('hourlyRate.editAria', { from: formatDay(row.effectiveStart, lang) })}
                              data-testid={`hourly-rate-edit-${row.id}`}
                            >
                              {t('hourlyRate.edit')}
                            </Button>
                            <Button
                              variant="ghost"
                              size="xs"
                              iconName="Trash2"
                              disabled={savingRate || draft !== null}
                              onClick={() => setConfirmDeleteId(row.id)}
                              aria-label={t('hourlyRate.deleteAria', { from: formatDay(row.effectiveStart, lang) })}
                              data-testid={`hourly-rate-delete-${row.id}`}
                            >
                              {t('hourlyRate.delete')}
                            </Button>
                          </span>
                        )}
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
          {rateError && !draft && (
            <p role="alert" className="border-t border-border px-5 py-3 text-sm text-destructive-strong">
              {rateError}
            </p>
          )}
          <p className="border-t border-border px-5 py-3 text-xs text-muted-foreground">
            {t('hourlyRate.currencyNote', { currency: companyCurrencyCode ?? EMPTY_VALUE })}
          </p>
        </section>
      </div>
    );
  };

  return (
    <div className="flex flex-1 flex-col">
      <UserProfile>
        <UserPayroll>{body()}</UserPayroll>
      </UserProfile>
    </div>
  );
};

export default HourlyPay;
